/* ============================================================================
 *  ANS · Fecha de envío y entrega
 *  Reemplaza a `estimated-delivery.js`, y es la ÚNICA fuente: el cajón de
 *  UpCart llama a `window.ansFechaEnvio()` en vez de llevar su propia copia.
 * ============================================================================
 *
 *  QUÉ RESUELVE, con lo medido sobre 1,384 pedidos reales (jul–oct 2026):
 *
 *   - La regla anterior NUNCA prometía sábado, aunque el 11.8% de los envíos
 *     salen en sábado. Y mandaba el jueves por la tarde hasta el lunes,
 *     saltándose el viernes que ella misma reconocía como hábil en otra rama.
 *   - Usaba `new Date().getHours()`: la hora del NAVEGADOR de la clienta. Una
 *     compradora en Cancún y otra en Tijuana veían promesas distintas en el
 *     mismo instante, y ninguna coincidía con el reloj del almacén.
 *   - Decía «Miécoles», sin la erre.
 *
 *  EL RELOJ ES EL DEL ALMACÉN. Guadalajara (UTC-6). La tienda está en La Paz
 *  (UTC-7): una hora de diferencia que mueve de lado la frontera del corte.
 *  Se calcula con `Intl`, que conoce la zona; nunca restando horas a mano.
 *
 *  CALENDARIO: lunes a sábado, 10:00–18:00. Domingo cerrado — medido: 0% de
 *  los envíos salieron en domingo en ninguna de las ventanas revisadas,
 *  incluida la semana del Buen Fin.
 *
 *  CORTE a las 15:00 GDL: antes, la mediana de salida es el MISMO día;
 *  después, salta a 1 día.
 *
 *  TRÁNSITO de 2 a 7 días hábiles: medido sobre 1,174 entregas con 99.3% de
 *  cobertura — mediana 3, p90 6, y el 92.4% cae dentro de esa ventana.
 *
 *  Desde el 8-oct-2026 la interfaz NO muestra esa ventana: sólo la fecha de
 *  salida. El cálculo se queda porque está medido y `ansFechaEnvio()` es pública
 *  — borrarlo tiraría 1,174 entregas de evidencia para ahorrar cuatro líneas.
 *
 *  LÍMITE CONOCIDO: en temporada alta esto se queda corto. En la semana del
 *  Buen Fin de 2025, CERO de 487 pedidos salieron el mismo día. Ninguna regla
 *  de calendario aguanta un pico; lo que la rompe es el volumen, no el mes.
 *  Queda pendiente que la fecha mire la cola real de pedidos sin surtir.
 * ========================================================================= */
(function (global) {
  'use strict';

  var ZONA = 'America/Mexico_City';
  var CORTE = 15;                    // hora GDL a partir de la cual el pedido ya no sale hoy
  var TRANSITO_MIN = 2;              // días hábiles al domicilio
  var TRANSITO_MAX = 7;

  /* Días en que el almacén NO trabajó, derivados de los pedidos reales (un día
   * sin un solo surtido en todo el periodo). No es una lista de festivos
   * oficiales: es lo que de verdad hizo el almacén. */
  var CERRADOS = ['2025-12-24', '2025-12-25', '2026-01-01', '2026-05-01', '2026-09-16',
                  '2026-12-24', '2026-12-25', '2027-01-01'];
  /* Hasta dónde alcanza esa lista. Pasada esta fecha el cálculo sigue siendo
   * correcto para domingos, pero puede prometer un festivo: avisa en consola en
   * vez de callarse, que es como estas listas se pudren sin que nadie lo note. */
  var CERRADOS_VIGENTES_HASTA = '2027-01-31';
  var avisado = false;

  var DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  var MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
               'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  var PARTES = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hour12: false, weekday: 'short'
  });
  var DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /** Un instante, leído con el reloj del almacén. */
  function enGdl(instante) {
    var p = {};
    PARTES.formatToParts(instante).forEach(function (x) { p[x.type] = x.value; });
    return {
      fecha: p.year + '-' + p.month + '-' + p.day,
      hora: Number(p.hour) % 24,          // 'en-CA' puede dar 24 a medianoche
      dow: DOW[p.weekday]
    };
  }

  /** Suma días a una fecha YYYY-MM-DD. Se opera a mediodía UTC para que ningún
   *  cambio de horario pueda mover el día de un lado al otro. */
  function masDias(fecha, n) {
    var t = Date.parse(fecha + 'T12:00:00Z') + n * 86400000;
    return new Date(t).toISOString().slice(0, 10);
  }

  function diaSemana(fecha) {
    return new Date(Date.parse(fecha + 'T12:00:00Z')).getUTCDay();
  }

  /** ¿Trabaja el almacén ese día? Lunes a sábado, salvo cierre conocido. */
  function esHabil(fecha) {
    if (diaSemana(fecha) === 0) return false;
    return CERRADOS.indexOf(fecha) === -1;
  }

  function siguienteHabil(fecha) {
    for (var i = 1; i <= 14; i++) {
      var f = masDias(fecha, i);
      if (esHabil(f)) return f;
    }
    return masDias(fecha, 1);   // red de seguridad: nunca devolver null
  }

  function masHabiles(fecha, n) {
    var f = fecha, c = 0, guarda = 0;
    while (c < n && guarda++ < 60) {
      f = masDias(f, 1);
      if (esHabil(f)) c++;
    }
    return f;
  }

  /** EL CÁLCULO. Puro: recibe el instante, devuelve fechas. Sin DOM, sin reloj
   *  propio — por eso se puede probar. */
  function calcula(instante) {
    var ahora = enGdl(instante || new Date());
    var sale = (esHabil(ahora.fecha) && ahora.hora < CORTE)
      ? ahora.fecha
      : siguienteHabil(ahora.fecha);

    /* Una sola vez por carga: un aviso que sale en cada repintado es ruido, y
     * el ruido se ignora — que es justo lo contrario de lo que hace un aviso. */
    if (sale > CERRADOS_VIGENTES_HASTA && !avisado && typeof console !== 'undefined' && console.warn) {
      avisado = true;
      console.warn('[ans-fecha-envio] la lista de días cerrados sólo llega al ' +
        CERRADOS_VIGENTES_HASTA + '; hay que volver a medirla con ' +
        '.claude/scripts/shopify-studio/envios/cuando-se-envia.mjs');
    }

    return {
      sale: sale,
      hoyEsElDia: sale === ahora.fecha,
      llegaDesde: masHabiles(sale, TRANSITO_MIN),
      llegaHasta: masHabiles(sale, TRANSITO_MAX)
    };
  }

  /* ---- redacción ---------------------------------------------------------- */

  function dia(fecha) { return new Date(Date.parse(fecha + 'T12:00:00Z')).getUTCDate(); }
  function mes(fecha) { return MESES[new Date(Date.parse(fecha + 'T12:00:00Z')).getUTCMonth()]; }

  /** «miércoles 7 de octubre» */
  function largo(fecha) {
    return DIAS[diaSemana(fecha)] + ' ' + dia(fecha) + ' de ' + mes(fecha);
  }

  function textos(r) {
    var salida = r.hoyEsElDia ? 'Sale hoy, ' + largo(r.sale) : 'Sale el ' + largo(r.sale);
    /* Si caen en el mismo mes no se repite: «entre el 9 y el 15 de octubre». */
    var llegada = mes(r.llegaDesde) === mes(r.llegaHasta)
      ? 'Te llega entre el ' + dia(r.llegaDesde) + ' y el ' + dia(r.llegaHasta) + ' de ' + mes(r.llegaHasta)
      : 'Te llega entre el ' + dia(r.llegaDesde) + ' de ' + mes(r.llegaDesde) +
        ' y el ' + dia(r.llegaHasta) + ' de ' + mes(r.llegaHasta);
    return { salida: salida, llegada: llegada };
  }

  /* ---- lo que consumen el tema y UpCart ----------------------------------- */

  function ansFechaEnvio(instante) {
    var r = calcula(instante);
    var t = textos(r);
    return {
      sale: r.sale, llegaDesde: r.llegaDesde, llegaHasta: r.llegaHasta,
      hoyEsElDia: r.hoyEsElDia, salidaTexto: t.salida, llegadaTexto: t.llegada
    };
  }

  function pinta() {
    var r = ansFechaEnvio();
    var salidas = document.querySelectorAll('[data-ans-salida]');
    var i;
    for (i = 0; i < salidas.length; i++) salidas[i].textContent = r.salidaTexto;

    /* La fecha de LLEGADA ya no se muestra (decisión de Enrique, 8-oct-2026: la
     * tienda promete lo que controla — cuándo sale de nuestro almacén — y no lo
     * que depende de la paquetería).
     *
     * El hueco se OCULTA en vez de confiar en que desaparezca del marcado, porque
     * el del cajón lo sirve UpCart desde su panel y no se despliega con el tema:
     * habría una ventana, de horas o de días, con la tienda diciendo una cosa en la
     * ficha y otra en el cajón. Así el cambio es simultáneo en las dos superficies.
     * Se oculta, no se borra, para no pelearnos con el re-dibujado de UpCart.
     * Cuando ese marcado se limpie, esto queda en nada. */
    var llegadas = document.querySelectorAll('[data-ans-llegada]');
    for (i = 0; i < llegadas.length; i++) {
      llegadas[i].textContent = '';
      llegadas[i].style.display = 'none';
    }

    /* Compatibilidad con el marcado viejo mientras conviva: ahí sólo cabe la
     * fecha de salida, sin la frase. `querySelectorAll` y no `getElementById`
     * porque ese id está duplicado en dos snippets del tema. */
    var viejos = document.querySelectorAll('#date__estimated, [data-ans-fecha]');
    for (i = 0; i < viejos.length; i++) viejos[i].textContent = largo(r.sale);
  }

  global.ansFechaEnvio = ansFechaEnvio;
  global.ansPintaFechaEnvio = pinta;

  /* El cajón de UpCart se dibuja cuando la clienta lo abre, mucho después de que
   * esto corrió. La primera versión lo resolvía reintentando unos segundos: eso es
   * una apuesta sobre el reloj, y en un teléfono lento se pierde — que es
   * exactamente lo que paso en vivo, con la banda dibujada y vacia.
   *
   * Un observador no apuesta: pinta el hueco en el momento en que aparece, lo
   * tarde que tarde. Y asi UpCart no necesita JavaScript propio: solo el marcado. */
  function vigila() {
    if (typeof MutationObserver !== 'function' || !document.body) return;
    var pendiente = false;
    new MutationObserver(function (cambios) {
      if (pendiente) return;
      for (var i = 0; i < cambios.length; i++) {
        var n = cambios[i].addedNodes;
        for (var j = 0; j < n.length; j++) {
          var el = n[j];
          if (el.nodeType !== 1) continue;
          if (el.matches && (el.matches('[data-ans-salida],[data-ans-llegada]') ||
              el.querySelector('[data-ans-salida],[data-ans-llegada]'))) {
            pendiente = true;
            /* Un tic de espera: UpCart mete el bloque entero de golpe y no hace
             * falta repintar una vez por cada nodo que inserta. */
            setTimeout(function () { pendiente = false; pinta(); }, 0);
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function arranca() { pinta(); vigila(); }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', arranca);
    } else {
      arranca();
    }
  }

  /* Para la prueba en node, sin tocar nada del navegador. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ansFechaEnvio: ansFechaEnvio, calcula: calcula, textos: textos,
                       esHabil: esHabil, largo: largo, enGdl: enGdl };
  }
})(typeof window !== 'undefined' ? window : globalThis);
